import React, {useEffect, useMemo, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import moment from 'moment-timezone';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

function getDecisionTone(decision) {
  switch (decision) {
    case 'BUY':
      return {bg: 'rgba(57, 217, 138, 0.12)', color: '#39D98A', border: 'rgba(57, 217, 138, 0.28)'};
    case 'SELL':
      return {bg: 'rgba(255, 107, 107, 0.12)', color: '#FF6B6B', border: 'rgba(255, 107, 107, 0.28)'};
    default:
      return {bg: 'rgba(245, 195, 107, 0.12)', color: '#F5C36B', border: 'rgba(245, 195, 107, 0.28)'};
  }
}

const panelSx = {
  borderRadius: 5,
  background: 'rgba(16, 29, 51, 0.84)',
  border: '1px solid rgba(126, 144, 168, 0.16)',
  boxShadow: '0 18px 45px rgba(0, 0, 0, 0.24)',
};

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const fetchOverview = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/dashboard/overview');
        if (active) {
          setOverview(response.data);
        }
      } catch (fetchError) {
        console.error(fetchError);
        if (active) {
          setError(t('dashboard.loadError'));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchOverview();

    return () => {
      active = false;
    };
  }, [t]);

  const topOpportunities = useMemo(
    () => overview?.top_opportunities || [],
    [overview],
  );

  const trackedAssets = useMemo(
    () => overview?.tracked_assets || [],
    [overview],
  );

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 12}}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !overview) {
    return (
      <Container maxWidth="xl" sx={{py: 5}}>
        <Alert severity="error">{error || t('dashboard.unavailable')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{py: { xs: 3, md: 5 }}}>
      <Stack spacing={4}>
        <Paper
          sx={{
            ...panelSx,
            overflow: 'hidden',
            background:
              isDark
                ? 'radial-gradient(circle at top left, rgba(88,196,221,0.20), transparent 32%), linear-gradient(160deg, rgba(10,21,39,0.98) 0%, rgba(16,29,51,0.94) 55%, rgba(20,34,57,0.96) 100%)'
                : 'radial-gradient(circle at top left, rgba(17,127,164,0.12), transparent 32%), linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(243,248,253,0.96) 55%, rgba(236,243,250,0.98) 100%)',
          }}>
          <Grid container spacing={0}>
            <Grid item xs={12} md={8}>
              <Box sx={{p: {xs: 3, md: 5}, height: '100%'}}>
                <Chip
                  label={t('dashboard.heroEyebrow')}
                  sx={{
                    mb: 2,
                    backgroundColor: 'rgba(88, 196, 221, 0.12)',
                    color: '#58C4DD',
                    border: '1px solid rgba(88, 196, 221, 0.25)',
                  }}
                />
                <Typography variant="h3" sx={{fontWeight: 800, maxWidth: 720, color: 'text.primary'}}>
                  {t('dashboard.heroTitle')}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{mt: 2, maxWidth: 680, lineHeight: 1.6}}>
                  {t('dashboard.heroSubtitle')}
                </Typography>

                <Stack direction="row" spacing={2} sx={{mt: 4}} flexWrap="wrap" useFlexGap>
                  <Button component={RouterLink} to="/predictions" variant="contained">
                    {t('dashboard.openDecisionCenter')}
                  </Button>
                  {topOpportunities[0] ? (
                    <Button
                      component={RouterLink}
                      to={`/assets/${topOpportunities[0].symbol}`}
                      variant="outlined"
                      color="inherit"
                      sx={{ color: 'text.primary' }}>
                      {t('dashboard.reviewAsset', { symbol: topOpportunities[0].symbol })}
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: {xs: 3, md: 4},
                  height: '100%',
                  backgroundColor: isDark ? 'rgba(7, 17, 31, 0.55)' : 'rgba(247, 251, 255, 0.7)',
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  {t('dashboard.marketBalance')}
                </Typography>
                <Stack spacing={2.5} sx={{mt: 1.5}}>
                  <Box>
                    <Typography variant="h4" sx={{fontWeight: 700, color: 'text.primary'}}>
                      {overview.market_summary.tracked_assets}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.trackedAssetsLabel')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{fontWeight: 700, color: '#39D98A'}}>
                      {overview.market_summary.bullish_decisions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.bullishLabel')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{fontWeight: 700, color: '#FF6B6B'}}>
                      {overview.market_summary.bearish_decisions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.bearishLabel')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{fontWeight: 700, color: 'text.primary'}}>
                      {overview.market_summary.average_sentiment}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.averageSentimentLabel')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {topOpportunities.map(asset => {
            const tone = getDecisionTone(asset.decision);
            return (
              <Grid item xs={12} md={4} key={asset.symbol}>
                <Paper sx={{...panelSx, p: 3.5, height: '100%', background: isDark ? 'rgba(16, 29, 51, 0.84)' : 'rgba(255,255,255,0.88)'}}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h5" sx={{fontWeight: 700}}>
                        {asset.symbol}
                      </Typography>
                      <Chip
                        label={asset.decision}
                        sx={{
                          backgroundColor: tone.bg,
                          color: tone.color,
                          border: `1px solid ${tone.border}`,
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                    <Typography variant="body1" color="text.secondary">
                      {t('dashboard.convictionLine', {
                        label: asset.confidence_label,
                        score: asset.confidence_score,
                      })}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={t('dashboard.sentimentChip', { label: asset.sentiment_label })} />
                      <Chip
                        label={t('dashboard.change24h', {
                          value: `${asset.price_change_24h >= 0 ? '+' : ''}${asset.price_change_24h}`,
                        })}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {asset.explanations?.[0]}
                    </Typography>
                    <Button component={RouterLink} to={`/assets/${asset.symbol}`} variant="text" sx={{px: 0}}>
                      {t('dashboard.fullIntelligence')}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{...panelSx, p: 3.5}}>
              <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                {t('dashboard.opportunitiesToReview')}
              </Typography>
              <Grid container spacing={2}>
                {trackedAssets.map(asset => {
                  const tone = getDecisionTone(asset.decision);
                  return (
                    <Grid item xs={12} md={6} key={asset.symbol}>
                      <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        backgroundColor: isDark ? 'rgba(7,17,31,0.45)' : 'rgba(241,246,252,0.92)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" sx={{fontWeight: 700}}>
                              {asset.symbol}
                            </Typography>
                            <Chip
                              label={asset.decision}
                              size="small"
                              sx={{
                                backgroundColor: tone.bg,
                                color: tone.color,
                                border: `1px solid ${tone.border}`,
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {asset.confidence_score}/100 · {t('dashboard.sentimentChip', { label: asset.sentiment_label })} · {t('dashboard.change24h', {
                              value: `${asset.price_change_24h >= 0 ? '+' : ''}${asset.price_change_24h}`,
                            })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {asset.explanations?.[1]}
                          </Typography>
                          <Button
                            component={RouterLink}
                            to={`/assets/${asset.symbol}`}
                            variant="outlined"
                            color="inherit"
                            sx={{alignSelf: 'flex-start', mt: 0.5, color: 'text.primary'}}>
                            {t('dashboard.inspectAsset')}
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              <Paper sx={{...panelSx, p: 3.5}}>
                <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                  {t('dashboard.sentimentPulse')}
                </Typography>
                <Stack spacing={1.25}>
                  {overview.sentiment_overview.slice(0, 5).map(item => (
                    <Stack
                      key={item.symbol}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center">
                      <Typography variant="body1" sx={{ color: 'text.primary' }}>{item.symbol}</Typography>
                      <Chip
                        size="small"
                        label={`${item.sentiment_score} ${item.sentiment_label}`}
                        sx={{
                          backgroundColor:
                            item.sentiment_score > 0
                              ? 'rgba(57, 217, 138, 0.12)'
                              : item.sentiment_score < 0
                                ? 'rgba(255, 107, 107, 0.12)'
                                : 'rgba(245, 195, 107, 0.12)',
                          color:
                            item.sentiment_score > 0
                              ? '#39D98A'
                              : item.sentiment_score < 0
                                ? '#FF6B6B'
                                : '#F5C36B',
                        }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Paper sx={{...panelSx, p: 3.5}}>
                <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                  {t('dashboard.latestDecisionFlow')}
                </Typography>
                <List disablePadding>
                  {overview.recent_decisions.slice(0, 8).map(item => (
                    <ListItem
                      key={`${item.symbol}-${item.timestamp}`}
                      disableGutters
                      component={RouterLink}
                      to={`/assets/${item.symbol}`}
                      sx={{textDecoration: 'none', color: 'inherit'}}>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Typography sx={{fontWeight: 700, color: 'text.primary'}}>{item.symbol}</Typography>
                            <Chip size="small" label={item.decision} />
                          </Stack>
                        }
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                        secondary={moment(item.timestamp).locale(i18n.language === 'gr' ? 'el' : 'en').fromNow()}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
};

export default Dashboard;
